import type { TFunction } from 'i18next';
import type { Delivery, InventoryLevel } from '../interfaces/ILogistics.js';
import { addDays, zonedDay } from './format.js';
import { carriedNote } from './carriedNote.js';
import { orderLinesFor, type IOrderLine } from './lines.js';
import { fold as foldName } from '../utils/text.js';

/** Seed values for a CREATE in the delivery dialog — ONE line's worth
 *  (`itemName`/`quantity`/`unit`) plus the order fields around it. Every field is something the
 *  page can honestly infer — nothing is invented. */
/** The delivery a carried-forward value came from, so the footnote under it can
 *  name the order rather than gesturing at "last time": its ref and the day it
 *  landed — or the day it was requested, for one still in flight. */
export interface IPrefillSource {
  ref: string;
  /** A bare calendar day, already resolved in the project's zone. */
  day: string;
}

export type OrderPrefill = {
  itemName: string;
  destinationName: string;
  /** Which delivery `destinationName` was read off, or null when none supplied
   *  one (and when the one that did can date itself neither by arrival nor by
   *  request — there is then nothing to name it by). */
  /** Where the destination came from: a past delivery (named in the footnote),
   *  `'task'` for the Scheduler task's own floor and zone (a Stock-tab
   *  hand-off — "Where this task works"), or null for neither. */
  destinationSource: IPrefillSource | 'task' | null;
  quantity: number | null;
  unit: string | null;
  requestedDate: string;
  dueDate: string;
  /** Passed through so the dialog can explain the figures rather than just
   *  presenting them: both are what `quantity` and `requestedDate` derive from. */
  leadTimeDays: number | null;
  dailyRate: number;
  deliveryOrder: string;
  /** The reorder line on its OWN — the sentence `deliveryOrder` opens with
   *  before any carried-forward note. Empty when the material has no stock row
   *  to size a reorder against. An order carrying several materials writes one
   *  of these per material instead of one material's whole `deliveryOrder`,
   *  which would drag that material's last dispatch note along with it. */
  reorderNote: string;
  /** The Stock tab's task sentence (`fromTaskNote`), on a task hand-off's
   *  lines only. Every line of one hand-off carries the same one, and an
   *  order carrying several of them writes it once (contract v14). */
  taskNote?: string;
  /** Who supplied the material on its most recent order that named one — a
   *  fact about the truck, carried forward like the destination. Absent on a
   *  task hand-off, which reads no delivery history. */
  supplierId?: string | null;
  /** The order `supplierId` was read off, for the footnote that names it —
   *  null when none named one, or when that order cannot date itself. */
  supplierSource?: IPrefillSource | null;
};


/**
 * What the "Order now" line opens the delivery dialog with.
 *
 * - **destination** — where this material was last delivered. Site logistics are
 *   habitual: the same material lands at the same yard. Blank when the site has
 *   no history for it rather than guessing another material's destination.
 * - **requestedDate** — today, in the PROJECT's zone: the day the request is
 *   raised, a fact about this order.
 * - **dueDate** — the ETA: today plus this material's lead time, the day the
 *   delivery is estimated to arrive (ruling 2026-09-15). Never the run-out day
 *   — the ETA says when the truck comes, not when the stock ends; the footnote
 *   beside it ("Arrival estimated to take Nd") describes exactly this sum.
 *   Blank when no lead time is known: there is then nothing to estimate.
 * - **note** — the reorder reason, a blank line, then what a PERSON wrote on
 *   the previous delivery's dispatch note (`carriedNote`: the lines the system
 *   itself put there are taken out first). Those notes are standing
 *   instructions (gate access, crane booking, who to call) that stay true order
 *   to order, so retyping them is wasted work — but the reason for THIS order
 *   goes first, since it is the part that changed. The kept lines are carried
 *   verbatim; they are the site's own words and this code has no business
 *   editing them.
 *
 * No status of any kind: "Order now" opens the form, it does not contact
 * anyone, and a new order always starts in the board's first column — only the
 * server decides that.
 */
/** A material's own delivery LINES, most-recent-first — the base for every
 *  "carry the last known X forward" lookup. Folded match on the line's item
 *  name (so width/case variants share one history); rows without a `createdAt`
 *  sort last, since they carry no date to trust. `deliveries` arrives
 *  urgency-sorted, not by date, so it must be re-sorted here. */
function pastDeliveriesFor(name: string, deliveries: Delivery[]): IOrderLine[] {
  return orderLinesFor(deliveries, name).sort((a, b) =>
    (b.order.createdAt ?? '').localeCompare(a.order.createdAt ?? ''),
  );
}

export function buildOrderPrefill(
  level: InventoryLevel,
  deliveries: Delivery[],
  tz: string,
  t: TFunction,
): OrderPrefill {
  const past = pastDeliveriesFor(level.materialName, deliveries);

  // Each field comes from the most recent order that actually HAS it — an older
  // order with real dispatch instructions is more useful than a newer blank one,
  // and the two need not come from the same row.
  const destinationFrom = past.find(({ order }) => order.destinationName.trim() !== '')?.order ?? null;
  const lastNote = past.map(({ order }) => carriedNote(order.deliveryOrder ?? '', t)).find((n) => n !== '') ?? '';

  const today = zonedDay(new Date().toISOString(), tz);
  const reason = t('logistics.orderDialog.reorderNote', {
    material: level.materialName,
    days: level.needsCheck ? 0 : level.daysLeft,
    lead: level.leadTimeDays ?? 0,
  });

  return {
    itemName: level.materialName,
    destinationName: destinationFrom?.destinationName ?? '',
    destinationSource: destinationFrom === null ? null : sourceOf(destinationFrom, tz),
    // Enough to outlast the NEXT resupply, not just to restore the usual pile.
    // An order smaller than `rate × lead` puts the site straight back into the
    // same shortage: it is consumed before a replacement can arrive, every time.
    // The usual amount still wins when it is already bigger — this raises a
    // floor, it does not impose a size.
    quantity: coveringQuantity(level),
    leadTimeDays: level.leadTimeDays,
    dailyRate: level.dailyRate,
    unit: level.unit,
    // The day the request is raised: today. A fact about this order, not a guess
    // about anything.
    requestedDate: today,
    // The estimated arrival — see the doc above. Until 2026-09-15 this was
    // the later of the run-out day and the lead, which put a date three months
    // out under a footnote explaining a 13-day lead.
    dueDate: level.leadTimeDays == null ? '' : addDays(today, level.leadTimeDays),
    // Blank line between the two, and no trailing whitespace when there is no
    // previous note to carry.
    deliveryOrder: lastNote ? `${reason}\n\n${lastNote}` : reason,
    reorderNote: reason,
    ...lastSupplier(past, tz),
  };
}

/** The material-habitual fields picking an existing material fills in — unit,
 *  destination, supplier, the covering quantity and the reorder note — and, for a
 *  tracked material, the same day-it-is-needed-by and lead figures "Order now"
 *  seeds (ruling 2026-09-15: picking a material in "+ New delivery" must look
 *  exactly like "Order now" on its stock row). One shape, one prefill. */
export type PickPrefill = OrderPrefill;

/**
 * What choosing an existing material from the delivery dialog's own suggestions
 * fills in.
 *
 * A TRACKED material IS the inventory row's "Order now": the full
 * `buildOrderPrefill`, dates and lead included. An UNTRACKED one — a delivery
 * exists but no stock is recorded, so there is no level to size a covering
 * quantity against — derives the habitual fields straight from its own
 * deliveries, newest-with-the-field winning (the same rule `buildOrderPrefill`
 * uses for destination and note); it has no lead to place a due date by, so
 * the date stays empty (ruled 2026-09-09) and the request day is today.
 */
export function buildPickPrefill(
  name: string,
  deliveries: Delivery[],
  inventory: InventoryLevel[],
  tz: string,
  t: TFunction,
): PickPrefill {
  const level = inventory.find((l) => foldName(l.materialName) === foldName(name));
  if (level) return buildOrderPrefill(level, deliveries, tz, t);
  const past = pastDeliveriesFor(name, deliveries);
  const destinationFrom = past.find(({ order }) => order.destinationName.trim() !== '')?.order ?? null;
  return {
    itemName: name,
    // Unit and amount come from the material's own LINE; destination and note
    // belong to the truck that carried it.
    unit: past.find(({ line }) => (line.unit ?? '') !== '')?.line.unit ?? null,
    destinationName: destinationFrom?.destinationName ?? '',
    destinationSource: destinationFrom === null ? null : sourceOf(destinationFrom, tz),
    quantity: past.find(({ line }) => line.quantity != null)?.line.quantity ?? null,
    deliveryOrder: past.map(({ order }) => carriedNote(order.deliveryOrder ?? '', t)).find((n) => n !== '') ?? '',
    requestedDate: zonedDay(new Date().toISOString(), tz),
    dueDate: '',
    leadTimeDays: null,
    dailyRate: 0,
    // No stock row, so no run-out to reorder against — there is no reorder
    // line to contribute to a multi-material note.
    reorderNote: '',
    ...lastSupplier(past, tz),
  };
}

/**
 * A past order that carried EXACTLY this set of materials, or null.
 *
 * The only honest source for a suggestion about a COMBINATION. Two materials
 * that each take three days apart may take eight together — one truck, one
 * supplier run, one unloading slot — and averaging their individual figures
 * invents a number nobody has ever observed. So the lookup is exact-set or
 * nothing: amounts and line order are ignored (the truck is the same truck
 * whether it carried four tonnes or six), but a third material makes it a
 * different combination.
 *
 * Arrived orders only — an order still in flight has no `deliveredAt` to
 * measure a lead against, and its destination is a plan rather than a fact.
 * The most recent arrival wins: this is the site's latest habit, not a mean.
 */
export interface IOrderPrecedent {
  ref: string;
  /** Its arrival, as a calendar day in the PROJECT's zone. */
  deliveredDay: string;
  destinationName: string;
  /** Its dispatch note, or '' when it carried none. */
  note: string;
  /** Who supplied it, or null when it named no supplier. */
  supplierId: string | null;
  /** Days from the day the request was raised to the day it landed, or null
   *  when it was never raised as a request — there is then nothing to measure
   *  from, and a lead of 0 would claim it arrived the day it was asked for. */
  leadDays: number | null;
}

export function precedentFor(
  materials: string[],
  deliveries: Delivery[],
  tz: string,
): IOrderPrecedent | null {
  const wanted = new Set(materials.map(foldName));
  let best: { order: Delivery; at: string } | null = null;
  for (const order of deliveries) {
    const at = order.deliveredAt;
    if (at === null || !carriesExactly(order, wanted)) continue;
    if (best === null || at > best.at) best = { order, at };
  }
  if (best === null) return null;
  const deliveredDay = zonedDay(best.at, tz);
  const requested = best.order.requestedDate;
  return {
    ref: best.order.ref,
    deliveredDay,
    destinationName: best.order.destinationName,
    note: best.order.deliveryOrder ?? '',
    supplierId: best.order.supplierId,
    leadDays: requested === null ? null : daysApart(requested, deliveredDay),
  };
}

/** The supplier of the material's most recent order that named one — the
 *  same newest-with-the-field rule the destination and note follow. */
function lastSupplier(past: IOrderLine[], tz: string): Pick<OrderPrefill, 'supplierId' | 'supplierSource'> {
  const from = past.find(({ order }) => order.supplierId !== null)?.order ?? null;
  return from === null
    ? { supplierId: null, supplierSource: null }
    : { supplierId: from.supplierId, supplierSource: sourceOf(from, tz) };
}

/** Which delivery a carried-forward value came from, for the footnote that
 *  names it. A landed order is dated by its arrival; one still in flight by
 *  the day it was requested. An order that can say neither is unciteable, and
 *  the footnote falls back to the version that names no order at all. */
function sourceOf(order: Delivery, tz: string): IPrefillSource | null {
  const day = order.deliveredAt === null ? order.requestedDate : zonedDay(order.deliveredAt, tz);
  return day === null ? null : { ref: order.ref, day };
}

/** The order's materials, as a set, against the wanted one — same fold every
 *  delivery↔material link on this page uses. */
function carriesExactly(order: Delivery, wanted: Set<string>): boolean {
  const carried = new Set(order.lines.map((line) => foldName(line.itemName)));
  return carried.size === wanted.size && [...carried].every((name) => wanted.has(name));
}

/** Whole days between two bare calendar days (b − a). Both are already
 *  zone-free, so this is a plain day count and no zone applies. */
function daysApart(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/**
 * The smallest order that outlasts its own resupply, or the usual amount —
 * whichever is larger.
 *
 * With no lead time there is nothing to clear, so the usual amount stands: a
 * floor computed from a guessed lead would be a fabricated requirement.
 */
function coveringQuantity(level: InventoryLevel): number {
  if (level.leadTimeDays == null || level.dailyRate <= 0) return level.amount;
  return Math.max(level.amount, Math.ceil(level.dailyRate * level.leadTimeDays));
}
